import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

export default function DriverDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDriverStatus, setSavingDriverStatus] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState(null);
  const [ratingDrafts, setRatingDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [driverRes, ordersRes] = await Promise.all([
      API.get(`drivers/${id}/`).catch(() => ({ data: null })),
      API.get('orders/').catch(() => ({ data: [] })),
    ]);

    const loadedDriver = driverRes.data || null;
    const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
    const driverOrders = loadedDriver
      ? allOrders.filter((o) => String(o.driver) === String(loadedDriver.id))
      : [];
    const nextDrafts = {};
    driverOrders.forEach((o) => {
      nextDrafts[o.id] = o.delivery_rating ?? '';
    });

    setDriver(loadedDriver);
    setOrders(driverOrders);
    setRatingDrafts(nextDrafts);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDriverStatus = async (newStatus) => {
    if (!driver?.id) return;
    setSavingDriverStatus(true);
    await API.post(`drivers/${driver.id}/set-status/`, { driver_status: newStatus }).catch(() => {});
    setSavingDriverStatus(false);
    load();
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setSavingOrderId(orderId);
    await API.post(`orders/${orderId}/update_status/`, { status: newStatus }).catch(() => {});
    setSavingOrderId(null);
    load();
  };

  const saveOrderRating = async (orderId) => {
    const raw = ratingDrafts[orderId];
    if (raw === '' || raw === null || raw === undefined) return;
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 5) {
      window.alert(t('Rating must be between 1 and 5'));
      return;
    }
    setSavingOrderId(orderId);
    await API.patch(`orders/${orderId}/`, { delivery_rating: parsed.toFixed(2) }).catch(() => {});
    setSavingOrderId(null);
    load();
  };

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [orders]);

  const ratingStars = (value) => {
    const score = Number(value || 0);
    const rounded = Math.round(score);
    return '★★★★★'.split('').map((star, idx) => (idx < rounded ? '★' : '☆')).join('');
  };

  const statusLabel = (status) => {
    const mapped = t(`status.${status}`);
    return mapped === `status.${status}` ? t(status) : mapped;
  };

  if (loading) {
    return (
      <Layout title={t('Driver Details')}>
        <div className="card">{t('Loading...')}</div>
      </Layout>
    );
  }

  if (!driver) {
    return (
      <Layout title={t('Driver Details')}>
        <div className="card">
          <p>{t('Driver not found')}</p>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/drivers')}>
            {t('Back to Drivers')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('Driver Details')}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row-between" style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>{driver.name}</h3>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/drivers')}>
            {t('Back to Drivers')}
          </button>
        </div>

        <div className="driver-summary-grid">
          <div><strong>{t('Phone')}:</strong> {driver.phone || '-'}</div>
          <div><strong>{t('Driver Code')}:</strong> {driver.driver_code || '-'}</div>
          <div><strong>{t('Status')}:</strong> {statusLabel(driver.driver_status || 'offline')}</div>
          <div><strong>{t('Rating')}:</strong> {Number(driver.overall_rating || 0).toFixed(2)}/5 {ratingStars(driver.overall_rating)}</div>
          <div><strong>{t('Delivery quality')}:</strong> {Number(driver.delivery_rating_avg || 0).toFixed(2)}</div>
          <div><strong>{t('Response speed')}:</strong> {Number(driver.response_rating || 0).toFixed(2)}</div>
          <div><strong>{t('Completion rate')}:</strong> {Number(driver.completion_rating || 0).toFixed(2)}</div>
          <div><strong>{t('Assigned orders count')}:</strong> {driver.total_assignments || 0}</div>
        </div>

        <div className="row-actions" style={{ marginTop: 12 }}>
          <strong>{t('Set Driver Status')}:</strong>
          <button type="button" className={`btn ${driver.driver_status === 'online' ? 'btn-primary' : 'btn-success'}`} onClick={() => updateDriverStatus('online')} disabled={savingDriverStatus}>{t('Online')}</button>
          <button type="button" className={`btn ${driver.driver_status === 'busy' ? 'btn-primary' : 'btn-warning'}`} onClick={() => updateDriverStatus('busy')} disabled={savingDriverStatus}>{t('Busy')}</button>
          <button type="button" className={`btn ${driver.driver_status === 'offline' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => updateDriverStatus('offline')} disabled={savingDriverStatus}>{t('Offline')}</button>
        </div>
      </div>

      <div className="card table-wrap">
        <h3 style={{ marginTop: 0 }}>{t('Driver Orders')}</h3>
        <table>
          <thead>
            <tr>
              <th>{t('Order Number')}</th>
              <th>{t('Sender')}</th>
              <th>{t('Recipient')}</th>
              <th>{t('Delivery Address')}</th>
              <th>{t('Status')}</th>
              <th>{t('Priority')}</th>
              <th>{t('Rating')}</th>
              <th>{t('Actions')}</th>
              <th>{t('Created At')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((o) => (
              <tr key={o.id}>
                <td>{o.order_number}</td>
                <td>{o.customer_name || '-'}</td>
                <td>{o.recipient_name || o.recipient_customer_name || '-'}</td>
                <td>{o.delivery_address || '-'}</td>
                <td><span className={`badge ${o.status}`}>{statusLabel(o.status)}</span></td>
                <td>{t(o.priority)}</td>
                <td>
                  <input
                    className="input"
                    type="number"
                    min="1"
                    max="5"
                    step="0.01"
                    value={ratingDrafts[o.id] ?? ''}
                    onChange={(e) => setRatingDrafts((prev) => ({ ...prev, [o.id]: e.target.value }))}
                    placeholder="1 - 5"
                    style={{ minWidth: 90 }}
                  />
                </td>
                <td>
                  <div className="row-actions">
                    <select
                      className="input"
                      value={o.status}
                      onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                      disabled={savingOrderId === o.id}
                      style={{ minWidth: 150 }}
                    >
                      <option value="offered">{t('Offered')}</option>
                      <option value="assigned">{t('Assigned')}</option>
                      <option value="picked_up">{t('Picked Up')}</option>
                      <option value="in_transit">{t('In Transit')}</option>
                      <option value="delivered">{t('Delivered')}</option>
                      <option value="cancelled">{t('Cancelled')}</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => saveOrderRating(o.id)}
                      disabled={savingOrderId === o.id}
                    >
                      {t('Save Rating')}
                    </button>
                  </div>
                </td>
                <td>{o.created_at ? new Date(o.created_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
            {sortedOrders.length === 0 && (
              <tr>
                <td colSpan={9}>{t('No orders assigned to this driver')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
